/**
 * Parse a Massachusetts Trial Court ("MassCourts" / Odyssey eServices) criminal
 * docket into structured data, and map it onto SpatchAI's Case state.
 *
 * The app never scrapes the portal or touches credentials: the attorney pastes
 * the docket text they are already authorized to view, and this runs entirely
 * on-device. See docs/masscourts-import-mapping.md for the field schema.
 *
 * Everything here is pure and string-only so it is unit-testable without a DOM.
 */

/** One charged count from the Charge tab. */
export interface DocketCharge {
  count: number;
  chapter: string; // MGL chapter, e.g. "265"
  section: string; // MGL section, e.g. "1" or "15D"
  subsection: string | null; // e.g. "b" from §15D(b)
  description: string; // "MURDER"
  degree: 'Felony' | 'Misdemeanor' | null;
  citation: string; // "c. 265, § 15D(b)"
  disposed: boolean; // nolle prossed / dismissed / etc.
  disposition: string | null; // "Nolle Prosequi"
}

/** A party and its lead attorney. */
export interface DocketParty {
  name: string;
  role: string; // "Prosecutor" | "Defendant" | "Keeper of Record" | ...
  /** Counsel of record NOW — the last one listed. See `attorneys`. */
  attorney: string | null;
  bbo: string | null;
  /**
   * Every attorney the docket lists for this party, oldest first.
   *
   * A case accumulates counsel over its life: ADAs rotate and defence counsel
   * substitutes, and MassCourts keeps them all, appending each new one. Taking
   * the FIRST entry — which this parser used to do — returns whoever was
   * assigned at arraignment, so a case could show an ADA who left the office
   * years ago as opposing counsel on a filing.
   */
  attorneys: Array<{ name: string; bbo: string | null }>;
}

/** A jury-selection signal extracted from the docket text. */
export interface DocketJurySignal {
  category:
    | 'change-of-venue'
    | 'questionnaire'
    | 'individual-voir-dire'
    | 'juror-information'
    | 'additional-challenges';
  date: string | null;
  ruling: string | null; // ALLOWED / DENIED / ...
  text: string;
}

/** Full parsed docket. */
export interface ParsedDocket {
  caseNumber: string | null;
  caption: string | null;
  defendant: string | null;
  caseType: string | null;
  caseStatus: string | null;
  fileDate: string | null;
  dcmTrack: string | null;
  initiatingAction: string | null;
  judge: string | null;
  nextEvent: string | null;
  charges: DocketCharge[];
  parties: DocketParty[];
  jurySelection: DocketJurySignal[];
}

/** The subset of Case state an import fills, plus derived summary counts. */
export interface CaseImport {
  caseName: string;
  court: string;
  county: string;
  docket: string;
  judge: string;
  /** The docket's "Next Event" date. MassCourts publishes no trial date. */
  trialDate: string;
  opp: string;
  prossBBO: string;
  defenseAtty: string;
  defBBO: string;
  /** The defendant's name as recorded on the docket, for the Parties tab. */
  defendantName: string;
  /** App-format charge strings for the live counts, e.g. "265-1 MURDER". */
  chargeTexts: string[];
  liveChargeCount: number;
  disposedChargeCount: number;
  jurySelection: DocketJurySignal[];
  /**
   * Charges that were on the Charges tab before this import and are NOT on the
   * docket, so they were removed. Filled in by importDocketText (not by the
   * parser, which sees only the docket) and reported in the import summary:
   * replacing the list silently would be its own kind of wrong.
   */
  droppedCharges?: string[];
}

const MA_COUNTIES = [
  'Barnstable',
  'Berkshire',
  'Bristol',
  'Dukes',
  'Essex',
  'Franklin',
  'Hampden',
  'Hampshire',
  'Middlesex',
  'Nantucket',
  'Norfolk',
  'Plymouth',
  'Suffolk',
  'Worcester'
];

const field = (text: string, re: RegExp): string | null => {
  const m = text.match(re);
  return m && m[1] != null ? m[1].trim() : null;
};
const clean = (s: string | null): string | null => {
  if (s == null) return null;
  const t = s.trim();
  return t.length ? t : null;
};


/** Header labels MassCourts prints, used to tell a value from the next label. */
const HEADER_LABELS = [
  'Case Type', 'Case Status', 'File Date', 'DCM Track', 'Initiating Action',
  'Status Date', 'Case Judge', 'Next Event'
];
const SECTION_WORDS =
  /^(All Information|Party|Charge|Event|Tickler|Docket|Disposition|Party Information|Party Charge Information|Alias|Party Attorney|Address|More Party Information)$/i;

/**
 * Fold real MassCourts output into the one-line shape the extractors expect.
 *
 * The portal renders each label and its value in separate elements, so copying
 * the page yields them on separate lines:
 *
 *     Case Type:            Charge #
 *     Indictment            1:
 *     Status Date:          265/1-0 -
 *     10/26/2023            Felony
 *                           MURDER c265 §1
 *
 * while the extractors were written against a sample where each was one line.
 * Tested against a live docket, that cost every header field, both dates, the
 * judge, and ALL charges — the caption and counsel came through, so it looked
 * like it had worked. Normalising here keeps the parsing rules in one shape
 * instead of making every regex line-aware.
 *
 * An empty field is preserved as empty: a label whose next line is another
 * label keeps no value, so "Case Judge:" followed by "Next Event:" still yields
 * a null judge rather than swallowing the next line.
 */
function normalizeDocketText(raw: string): string {
  const lines = raw.split('\n').map((l) => l.trim());
  const labelRe = new RegExp(`^(${HEADER_LABELS.join('|')})\\s*:?\\s*$`, 'i');
  // Matches a label line whether or not it already carries its value, and
  // whether or not MassCourts printed the colon (it omits it on File Date).
  // Without the value-bearing form, a one-line docket joined "Case Judge:"
  // to the whole "Next Event: ..." line that followed it.
  const anyLabelRe = new RegExp(`^(${HEADER_LABELS.join('|')})\\b\\s*:?`, 'i');
  const chargeEnd = /^(Original Charge|Indicted Charge|Amended Charge|Charge Disposition|Charge #)$/i;
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const lm = labelRe.exec(line);
    if (lm) {
      const next = lines[i + 1] ?? '';
      if (next && !anyLabelRe.test(next) && !SECTION_WORDS.test(next)) {
        out.push(`${lm[1]}: ${next}`);
        i++;
        continue;
      }
      out.push(`${lm[1]}:`);
      continue;
    }

    // "Charge #" / "1:" / "265/1-0 -" / "Felony" / "MURDER c265 §1" -> one line
    if (/^Charge #$/i.test(line)) {
      const parts = [line];
      let j = i + 1;
      while (j < lines.length && lines[j] && !chargeEnd.test(lines[j])) {
        parts.push(lines[j]);
        j++;
      }
      out.push(parts.join(' '));
      i = j - 1;
      continue;
    }

    out.push(line);
  }
  return out.join('\n');
}


/**
 * Work out who is actually presiding when the header's "Case Judge" is blank.
 *
 * MassCourts leaves that field empty on plenty of live cases — the real docket
 * this was built against has it empty while Hon. William F Sullivan has been
 * sitting on it for months. The judge IS on the page, just not in the header:
 * the Events table carries an "Event Judge" per hearing, and docket entries say
 * "<Name>, Presiding".
 *
 * Preference order, most authoritative first:
 *   1. The judge on the NEXT EVENT. That is the judge you will stand in front
 *      of, which is what "presiding" means to counsel preparing for it. Cases
 *      get reassigned — this docket shows Gildea sitting 07/20-07/24 and
 *      Sullivan from 07/27 — so the most recent judge is not always the next.
 *   2. Failing that, the most frequently recorded "Presiding" judge.
 *
 * Returned in the same "Surname, First M" shape the header field uses, with
 * "Hon." stripped: reports.ts prepends "Hon. " itself, and keeping it here
 * produced "Hon. Sullivan, Hon. William F".
 */
/**
 * A surname as MassCourts writes them: one token, optionally preceded by a
 * recognised particle — "St. Fleur", "Van Rader", "De La Cruz", "O'Brien",
 * "Smith-Jones".
 *
 * The particle list is a closed set on purpose. An earlier version allowed any
 * two extra capitalised words, and "Event Result:: Status Held Sullivan, Hon.
 * William F, Presiding" parsed as the surname "Status Held Sullivan". Docket
 * prose is full of capitalised words, so anything open-ended reads them as
 * names.
 */
const PARTICLE = "(?:St\\.|Ste\\.|Saint|Van|Von|Der|Den|De|Del|Della|Di|Da|Dos|La|Le|Du|Mac|Mc|O')";
const SURNAME = `(?:${PARTICLE}\\s+){0,2}[A-Z][A-Za-z'’-]+`;

/** "Surname, Hon. First M" — the Event Judge cell format. Returns null if no match. */
function matchEventJudge(cell: string): string | null {
  const m = new RegExp(`^(${SURNAME}),\\s*Hon\\.\\s*([A-Z][A-Za-z.]*(?:\\s+[A-Z][A-Za-z.]*){0,2})$`).exec(
    cell.trim()
  );
  return m ? `${m[1].trim()}, ${m[2].trim()}` : null;
}

export function stripHonorific(name: string): string {
  return name
    .replace(/\bHon\.\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/,\s*$/, '')
    .trim();
}

/**
 * The next scheduled event date, when the header's "Next Event:" is blank.
 *
 * MassCourts leaves that header field empty on plenty of live cases while the
 * Events table plainly lists a future hearing. It mattered twice over: the date
 * itself never reached the Case tab, AND inferPresidingJudge() anchors on it, so
 * a blank header silently emptied the Presiding Judge box as well.
 *
 * "Next" means the earliest event dated after `today`. Scoped to the Events
 * table — docket ENTRIES are a record of what already happened, and reading a
 * date from there would report a past hearing as the next one.
 *
 * `today` is injected rather than read from the clock so the behaviour is
 * testable and the parser stays pure.
 */
function inferNextEvent(text: string, today: Date): string | null {
  const region = section(text, /^Events\b/im, /Docket Information|Case Disposition|Party Charge/i);
  if (region === text) return null; // no Events table at all

  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  let best: { t: number; s: string } | null = null;
  for (const m of region.matchAll(/\b(\d{2})\/(\d{2})\/(\d{4})\b/g)) {
    const t = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])).getTime();
    if (Number.isNaN(t) || t < midnight) continue;
    if (!best || t < best.t) best = { t, s: m[0] };
  }
  return best ? best.s : null;
}

function inferPresidingJudge(text: string, nextEvent: string | null): string | null {
  // 1. the judge sitting on the next event.
  //
  // Real portal output puts an event on ONE tab-separated row as often as it
  // splits it across lines, so both shapes are searched: every cell of the
  // matching row, then the following lines up to the next dated row.
  if (nextEvent) {
    const lines = text.split('\n').map((l) => l.replace(/\s+$/, ''));
    const dateRe = /^\s*\d{2}\/\d{2}\/\d{4}\b/;
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].trim().startsWith(nextEvent)) continue;

      // same-row, tab-separated
      for (const cell of lines[i].split('\t')) {
        const hit = matchEventJudge(cell);
        if (hit) return hit;
      }
      // or on its own line below, until the next dated row
      for (let j = i + 1; j < lines.length && j < i + 8; j++) {
        if (dateRe.test(lines[j])) break;
        for (const cell of lines[j].split('\t')) {
          const hit = matchEventJudge(cell);
          if (hit) return hit;
        }
      }
    }
  }

  // 2. the most frequently recorded presiding judge.
  //
  // Both orders appear: "Hon. William F Sullivan, Presiding" (first-name-first,
  // needs reordering) and "Sullivan, Hon. William F, Presiding" (already in
  // surname-first shape). "Hon." is optional — some entries omit it — but the
  // literal "Presiding" or a "Judge:" label is required. Without one of those
  // markers this would start guessing at names in free text, which is how an
  // earlier version invented a judge from a disposition table.
  const tally = new Map<string, number>();
  const bump = (name: string): void => {
    const n = stripHonorific(name);
    if (n) tally.set(n, (tally.get(n) ?? 0) + 1);
  };

  for (const m of text.matchAll(
    new RegExp(`(?:Hon\\.\\s*)?(${SURNAME}),\\s*(?:Hon\\.\\s*)?([A-Z][A-Za-z.]*(?:\\s+[A-Z][A-Za-z.]*){0,2}),\\s*Presiding`, 'gi')
  )) {
    // Already surname-first — "Sullivan, Hon. William F, Presiding".
    bump(`${m[1]}, ${m[2]}`);
  }
  for (const m of text.matchAll(/Hon\.\s*([A-Z][A-Za-z.\s]+?),\s*Presiding/gi)) {
    // First-name-first — "Hon. William F Sullivan, Presiding" — needs flipping.
    const parts = stripHonorific(m[1]).split(/\s+/);
    if (parts.length < 2) continue;
    bump(`${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`);
  }
  for (const m of text.matchAll(new RegExp(`Judge:\\s*(${SURNAME},\\s*(?:Hon\\.\\s*)?[A-Z][A-Za-z.\\s]*)`, 'gi'))) {
    bump(m[1]);
  }

  if (!tally.size) return null;
  return [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * Parse the raw pasted docket text into a structured ParsedDocket.
 *
 * `today` decides which Events rows count as upcoming when the header carries
 * no "Next Event:" date. Injected so the parser stays pure and testable.
 */
export function parseDocket(raw: string, today: Date = new Date()): ParsedDocket {
  const text = normalizeDocketText(String(raw || '').replace(/\r\n?/g, '\n'));
  /**
   * Header fields are read from the top of the docket ONLY.
   *
   * MassCourts repeats these labels further down — the disposition table has
   * its own "Case Judge" column. Scanning the whole document, a case with no
   * judge assigned picked up "Active" from that table: a header field silently
   * answered by an unrelated row. The header ends where the tab strip begins.
   */
  const header = text.split(/\n(?:All Information|Party Information)\b/i)[0];

  // ── Header ──────────────────────────────────────────────────────────────
  // The header field when the court filled it in; otherwise the earliest
  // upcoming row of the Events table. Computed here rather than inline in the
  // return because the presiding-judge inference anchors on it: when this was
  // header-only, a blank "Next Event:" emptied the Judge box as well.
  const nextEvent =
    clean(field(header, /Next Event:[ \t]*(\d{2}\/\d{2}\/\d{4})/i)) ?? inferNextEvent(text, today);

  const capLine = text.match(/^\s*(\S+)\s+(Commonwealth\s+vs\.?\s+.+)$/im);
  const caseNumber = capLine ? capLine[1].trim() : field(text, /\b(\d{2}\d{2}CR\d{4,6})\b/);
  const caption = capLine ? capLine[2].trim() : null;
  const defendant = caption ? caption.replace(/^Commonwealth\s+vs\.?\s*/i, '').trim() : null;

  return {
    caseNumber,
    caption,
    defendant,
    caseType: clean(field(header, /Case Type:[ \t]*([^\n]+)/i)),
    caseStatus: clean(field(header, /Case Status:[ \t]*([^\n]+)/i)),
    fileDate: clean(field(header, /File Date:?[ \t]*(\d{2}\/\d{2}\/\d{4})/i)),
    dcmTrack: clean(field(header, /DCM Track:[ \t]*([^\n]+)/i)),
    initiatingAction: clean(field(header, /Initiating Action:[ \t]*([^\n]+)/i)),
    // Header field first; if the court has not filled it in, work it out from
    // the Events table and docket entries — see inferPresidingJudge().
    //
    // stripHonorific applies to BOTH paths. It used to be inside the inference
    // only, so a docket that DID fill in "Case Judge: Sullivan, Hon. William F"
    // kept the "Hon." and reports.ts — which prepends its own — rendered
    // "Hon. Sullivan, Hon. William F".
    judge: ((): string | null => {
      const explicit = clean(field(header, /Case Judge:[ \t]*([^\n]+)/i));
      const found = explicit ?? inferPresidingJudge(text, nextEvent);
      return found ? stripHonorific(found) || null : null;
    })(),
    nextEvent,
    charges: parseCharges(text),
    parties: parseParties(text),
    jurySelection: parseJurySelection(text)
  };
}

/** Slice `text` between the first `start` marker and the first later `end` marker. */
function section(text: string, start: RegExp, end: RegExp): string {
  const s = text.search(start);
  if (s < 0) return text;
  const rest = text.slice(s);
  const e = rest.search(end);
  return e < 0 ? rest : rest.slice(0, e);
}

function parseCharges(text: string): DocketCharge[] {
  const region = section(
    text,
    /Party Charge Information/i,
    /\n(?:Events|Ticklers|Docket Information)\b/i
  );
  const markers = [...region.matchAll(/Charge #\s*(\d+):\s*([^\n]+)/gi)];
  const charges: DocketCharge[] = [];
  for (let i = 0; i < markers.length; i++) {
    const m = markers[i];
    const count = parseInt(m[1], 10);
    const descLine = m[2].trim();
    const segEnd = i + 1 < markers.length ? markers[i + 1].index! : region.length;
    const segment = region.slice(m.index!, segEnd);

    const degreeM = descLine.match(/\b(Felony|Misdemeanor)\b/i);
    const degree = degreeM
      ? ((degreeM[1][0].toUpperCase() + degreeM[1].slice(1).toLowerCase()) as 'Felony' | 'Misdemeanor')
      : null;
    const stat = descLine.match(/c\.?\s*(\d+[A-Z]?)\s*§+\s*(\d+[A-Z]*)(?:\(([a-z0-9]+)\))?/i);
    const chapter = stat ? stat[1] : '';
    const secRaw = stat ? stat[2] : '';
    const subsection = stat && stat[3] ? stat[3] : null;
    // Crime name: between the degree word and the "c<chap>" fragment.
    const nameM = descLine.match(/(?:Felony|Misdemeanor)\s+(.+?)\s+c\.?\s*\d/i);
    const description = nameM ? nameM[1].trim() : descLine.replace(/^[\d/A-Z-]+\s*-\s*/i, '').trim();

    const dispM = segment.match(
      /(?:Disposition[^\n]*\n[^\n]*\n\s*(?:(\d{2}\/\d{2}\/\d{4})\s+)?)?\b(Nolle Prosequi|Dismissed(?:\s+at\s+Request)?|Guilty|Not Guilty|Placed on File|Filed|Decriminalized)\b/i
    );
    const disposition = dispM ? dispM[2].replace(/\s+/g, ' ').trim() : null;

    charges.push({
      count,
      chapter,
      section: secRaw,
      subsection,
      description,
      degree,
      citation: chapter
        ? `c. ${chapter}, § ${secRaw}${subsection ? `(${subsection})` : ''}`
        : descLine,
      disposed: disposition != null,
      disposition
    });
  }
  return charges;
}

function parseParties(text: string): DocketParty[] {
  const region = section(text, /Party Information/i, /Party Charge Information/i);
  const roleRe =
    /^(.+?)\s+-\s+(Prosecutor|Defendant|Keeper of Record|Petitioner|Respondent|Plaintiff|Subject|Other interested party|Defendant-Intervenor)\s*$/gim;
  const heads = [...region.matchAll(roleRe)];
  const parties: DocketParty[] = [];
  for (let i = 0; i < heads.length; i++) {
    const h = heads[i];
    const segEnd = i + 1 < heads.length ? heads[i + 1].index! : region.length;
    const segment = region.slice(h.index!, segEnd);
    // Anchor to line start so "District Attorney" in a party header line
    // doesn't shadow the real "Attorney <name>" line.
    //
    // Collect ALL of them, pairing each attorney with the Bar Code that follows
    // it. Pairing matters: taking the last "Attorney" and the last "Bar Code"
    // independently mismatches the two whenever one of the entries is missing a
    // bar number.
    const attorneys: Array<{ name: string; bbo: string | null }> = [];
    const attyRe = /^Attorney\s+([^\n]+)/gim;
    for (let m = attyRe.exec(segment); m; m = attyRe.exec(segment)) {
      const after = segment.slice(m.index + m[0].length);
      // Only look as far as the next Attorney entry, so a missing Bar Code does
      // not borrow the next attorney's.
      const nextAtty = after.search(/^Attorney\s+/im);
      const window = nextAtty < 0 ? after : after.slice(0, nextAtty);
      const bboM = window.match(/^Bar Code\s+(\d+)/im);
      attorneys.push({ name: m[1].trim(), bbo: bboM ? bboM[1].trim() : null });
    }

    // Counsel of record is the LAST entry: MassCourts appends, so the newest
    // substitution is at the bottom.
    const current = attorneys.length ? attorneys[attorneys.length - 1] : null;
    parties.push({
      name: h[1].trim(),
      role: h[2].trim(),
      attorney: current ? current.name : null,
      bbo: current ? current.bbo : null,
      attorneys
    });
  }
  return parties;
}

const JURY_PATTERNS: Array<{ category: DocketJurySignal['category']; re: RegExp }> = [
  { category: 'change-of-venue', re: /change of venue/i },
  { category: 'questionnaire', re: /jury questionnaire/i },
  {
    category: 'individual-voir-dire',
    re: /individual voir dire|venire individually|jury questions to be directed to the venire/i
  },
  { category: 'juror-information', re: /access to (?:prospective )?juror|jury venire|prospective juror information/i },
  { category: 'additional-challenges', re: /additional (?:peremptory )?challenges/i }
];

function parseJurySelection(text: string): DocketJurySignal[] {
  const out: DocketJurySignal[] = [];
  const seen = new Set<string>();
  for (const line of text.split('\n')) {
    const l = line.trim();
    if (!l) continue;
    for (const { category, re } of JURY_PATTERNS) {
      if (!re.test(l)) continue;
      const key = category + '|' + l.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const dateM = l.match(/(\d{2}\/\d{2}\/\d{4})/);
      const ruleM = l.match(/:\s*(ALLOWED|DENIED|Other action taken|No Action Taken)\b/i);
      out.push({
        category,
        date: dateM ? dateM[1] : null,
        ruling: ruleM ? ruleM[1] : null,
        text: l
      });
    }
  }
  return out;
}

/** Detect the first Massachusetts county named in the text. */
function detectCounty(text: string): string {
  const re = new RegExp(`\\b(${MA_COUNTIES.join('|')})\\s+County\\b`, 'i');
  const m = text.match(re);
  if (!m) return '';
  return m[1][0].toUpperCase() + m[1].slice(1).toLowerCase();
}

/**
 * Map a ParsedDocket onto the Case fields SpatchAI fills. Live (non-disposed)
 * charges become deduped app-format charge strings; disposed counts are dropped
 * from the working list but counted.
 */
export function toCaseImport(parsed: ParsedDocket, rawForCounty = ''): CaseImport {
  const pros = parsed.parties.find((p) => /Prosecutor/i.test(p.role));
  const def = parsed.parties.find((p) => /^Defendant$/i.test(p.role));

  const live = parsed.charges.filter((c) => !c.disposed && c.chapter && c.section);
  const seen = new Set<string>();
  const chargeTexts: string[] = [];
  for (const c of live) {
    const t = `${c.chapter}-${c.section} ${c.description}`.trim();
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    chargeTexts.push(t);
  }

  const county = detectCounty(rawForCounty || parsed.parties.map((p) => p.name).join(' '));
  const isSuperior = /Indictment/i.test(parsed.caseType || '');
  const court = county ? `${county} County ${isSuperior ? 'Superior Court' : 'Court'}` : '';

  return {
    caseName: parsed.caption || '',
    // The defendant's name as the docket gives it. Prefer the Party Information
    // row, which is the court's own record of the party, and fall back to the
    // caption ("Commonwealth vs. <name>") when the party block is missing.
    defendantName: def?.name || parsed.defendant || '',
    court,
    county,
    docket: parsed.caseNumber || '',
    judge: parsed.judge || '',
    trialDate: parsed.nextEvent || '',
    opp: pros?.attorney || '',
    prossBBO: pros?.bbo || '',
    defenseAtty: def?.attorney || '',
    defBBO: def?.bbo || '',
    chargeTexts,
    liveChargeCount: live.length,
    disposedChargeCount: parsed.charges.length - live.length,
    jurySelection: parsed.jurySelection
  };
}
